import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './DropdownMenu.css';

export interface DropdownMenuItem {
  id: string | number;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  divider?: boolean;
}

export interface DropdownMenuProps {
  // 菜单项列表
  items: DropdownMenuItem[];
  // 是否可见
  visible: boolean;
  // 关闭回调
  onClose: () => void;
  // 选择项回调
  onSelect: (item: DropdownMenuItem) => void;
  // 位置信息（用于定位菜单）
  position?: {
    top?: number | string;
    left?: number | string;
    right?: number | string;
    bottom?: number | string;
  };
  // 最大高度（默认 320px，参考 InputRecommend）
  maxHeight?: number;
  // 宽度（默认 auto）
  width?: number | string;
  // 是否使用固定定位（相对于视口，而不是父容器）
  useFixedPosition?: boolean;
  /** 将菜单挂到 document.body，避免 EditScene / 拍摄计划 等 overflow 与层叠上下文裁切固定定位子元素 */
  portalToBody?: boolean;
  // 当前选中的值（用于默认选中）
  selectedValue?: string | number | null;
  // 自定义类名（用于覆盖样式，如圆角）
  className?: string;
  // 下拉菜单的唯一标识符（用于 DOM 查找）
  dataDropdownId?: string;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  items,
  visible,
  onClose,
  onSelect,
  position = {},
  maxHeight = 320,
  width = 'auto',
  useFixedPosition = false,
  portalToBody = false,
  selectedValue,
  className,
  dataDropdownId
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const hasNavigatedRef = useRef<boolean>(false); // 跟踪用户是否用方向键导航过

  // 当 visible 或 items 变化时，根据 selectedValue 设置选中索引
  useEffect(() => {
    if (visible && items.length > 0) {
      // 重置导航标记
      hasNavigatedRef.current = false;
      // 如果有 selectedValue，找到对应的索引
      if (selectedValue !== undefined && selectedValue !== null) {
        const index = items.findIndex(item => item.id === selectedValue && !item.disabled);
        setSelectedIndex(index >= 0 ? index : -1);
      } else {
        setSelectedIndex(-1);
      }
    } else {
      setSelectedIndex(-1);
      hasNavigatedRef.current = false;
    }
  }, [visible, items, selectedValue]);

  // 键盘事件处理：上下方向键导航和回车键选择
  useEffect(() => {
    if (!visible || items.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        if (e.key === 'ArrowUp') {
          // 标记用户已用方向键导航
          hasNavigatedRef.current = true;
          setSelectedIndex(prev => {
            const enabledItems = items.filter((_, idx) => !items[idx].disabled);
            if (enabledItems.length === 0) return -1;
            
            let currentEnabledIndex = -1;
            for (let i = 0; i < items.length; i++) {
              if (!items[i].disabled) {
                if (i === prev) {
                  currentEnabledIndex = enabledItems.findIndex(item => item.id === items[i].id);
                  break;
                }
              }
            }
            
            if (currentEnabledIndex <= 0) {
              return items.findIndex(item => item.id === enabledItems[enabledItems.length - 1].id);
            }
            return items.findIndex(item => item.id === enabledItems[currentEnabledIndex - 1].id);
          });
        } else if (e.key === 'ArrowDown') {
          // 标记用户已用方向键导航
          hasNavigatedRef.current = true;
          setSelectedIndex(prev => {
            const enabledItems = items.filter((_, idx) => !items[idx].disabled);
            if (enabledItems.length === 0) return -1;
            
            let currentEnabledIndex = -1;
            for (let i = 0; i < items.length; i++) {
              if (!items[i].disabled) {
                if (i === prev) {
                  currentEnabledIndex = enabledItems.findIndex(item => item.id === items[i].id);
                  break;
                }
              }
            }
            
            if (currentEnabledIndex < 0 || currentEnabledIndex >= enabledItems.length - 1) {
              return items.findIndex(item => item.id === enabledItems[0].id);
            }
            return items.findIndex(item => item.id === enabledItems[currentEnabledIndex + 1].id);
          });
        } else if (e.key === 'Enter') {
          // 如果用户用方向键导航过，选择当前选中的项
          if (hasNavigatedRef.current) {
            if (selectedIndex >= 0 && selectedIndex < items.length && !items[selectedIndex].disabled) {
              const item = items[selectedIndex];
              if (!item.disabled) {
                onSelect(item);
                setSelectedIndex(-1);
                hasNavigatedRef.current = false;
              }
            }
          } else {
            // 如果用户没有用方向键导航过，直接关闭菜单并退出编辑状态
            onClose();
          }
        } else if (e.key === 'Escape') {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [visible, items, selectedIndex, selectedValue, onSelect, onClose]);

  // 滚动到选中项
  useEffect(() => {
    if (selectedIndex >= 0 && itemsRef.current[selectedIndex]) {
      itemsRef.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedIndex]);

  // 点击外部区域关闭
  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // 检查点击的目标是否是下拉按钮本身（通过 data-field 属性）
      // 使用 closest 查找按钮，因为点击的可能是按钮内的子元素
      const dropdownButton = target.closest('.excel-cell-button, .scene-detail-select-button') as HTMLElement;
      const isDropdownButton = dropdownButton && (
        dropdownButton.getAttribute('data-field') === 'sceneInteriorExterior' ||
        dropdownButton.getAttribute('data-field') === 'sceneDayNight'
      );
      
      // 检查点击的目标是否在按钮容器内（表格模式）
      const isInTableCellWrapper = target.closest('.breakdown-editable-cell-wrapper') !== null;
      
      // 检查点击的目标是否在按钮容器内（详情页模式）
      const isInDetailContainer = target.closest('.scene-detail-input-container') !== null;
      
      // 如果点击的是下拉按钮或按钮容器，不关闭
      if (isDropdownButton || isInTableCellWrapper || isInDetailContainer) {
        return;
      }
      
      // 如果点击的是下拉菜单内部，不关闭
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }
      
      // 点击外部区域，关闭下拉菜单
      onClose();
      setSelectedIndex(-1);
    };
    // 使用 capture 阶段，确保在其他事件处理器之前执行
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [visible, onClose]);

  const handleSelect = (item: DropdownMenuItem) => {
    if (item.disabled) {
      return;
    }
    onSelect(item);
    setSelectedIndex(-1);
  };

  // 如果没有菜单项或组件不可见，不显示组件
  if (!visible || items.length === 0) {
    return null;
  }

  const style: React.CSSProperties = {
    ...position,
    width: typeof width === 'number' ? `${width}px` : width,
    maxHeight: `${maxHeight}px`,
    position: useFixedPosition ? 'fixed' : 'absolute'
  };

  const node = (
    <div 
      className={`dropdown-menu ${className || ''}`.trim()} 
      ref={containerRef} 
      style={style}
      {...(dataDropdownId ? { 'data-dropdown-id': dataDropdownId } : {})}
    >
      <div className="dropdown-menu-dialog" style={{ maxHeight: `${maxHeight}px` }}>
        <div className="dropdown-menu-items" style={{ maxHeight: `${maxHeight}px` }}>
          {items.map((item, index) => {
            if (item.divider) {
              return <div key={`divider-${index}`} className="dropdown-menu-divider" />;
            }
            
            const isSelected = selectedIndex === index;
            const isDisabled = item.disabled;

            return (
              <div
                key={item.id}
                ref={(el) => {
                  if (el) {
                    itemsRef.current[index] = el;
                  }
                }}
                className={`dropdown-menu-item ${isSelected ? 'dropdown-menu-item-selected' : ''} ${isDisabled ? 'dropdown-menu-item-disabled' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  // 选择处理已在 onMouseDown 中完成，这里只阻止事件冒泡
                }}
                onMouseDown={(e) => {
                  e.preventDefault(); // 阻止默认行为，防止按钮失去焦点
                  e.stopPropagation();
                  // 在 mousedown 中直接处理选择，确保在 blur 之前执行
                  if (!item.disabled) {
                    handleSelect(item);
                  }
                }}
              >
                <div className="dropdown-menu-info">
                  {item.icon && (
                    <div className="dropdown-menu-icon">
                      {item.icon}
                    </div>
                  )}
                  <div className="dropdown-menu-label">
                    {item.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (portalToBody && useFixedPosition && typeof document !== 'undefined') {
    return createPortal(node, document.body);
  }

  return node;
};

export default DropdownMenu;

